import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import PublicLayout from "@/components/layout/PublicLayout";
import SEO from "@/components/SEO";
import { Reveal } from "@/components/motion";
import { api } from "@/lib/api";

export default function Blog() {
  const [data, setData] = useState({ items: [], categories: [], total: 0 });
  const [cat, setCat] = useState("All");
  const [page, setPage] = useState(1);

  useEffect(() => {
    api.get(`/blog?page=${page}&limit=9${cat !== "All" ? `&category=${encodeURIComponent(cat)}` : ""}`)
      .then((r) => setData(r.data)).catch(() => {});
  }, [cat, page]);

  return (
    <PublicLayout>
      <SEO title="Blog | NewStarAds YouTube Marketing Insights" description="Practical, honest articles on YouTube marketing, ads, audience targeting, video promotion, creator growth and SEO from the NewStarAds team." />
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-8">
        <Reveal>
          <p className="text-xs uppercase tracking-widest font-semibold text-primary">Blog</p>
          <h1 className="mt-3 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter">YouTube marketing insights</h1>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl">Practical, honest advice on growing YouTube channels — no hype, no spam, no guaranteed-view myths.</p>
        </Reveal>
        <div className="mt-8 flex flex-wrap gap-2">
          {["All", ...(data.categories || [])].map((c) => (
            <button key={c} onClick={() => { setCat(c); setPage(1); }} data-testid={`blog-cat-${c}`}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${cat === c ? "bg-primary text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{c}</button>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {data.items.map((p, i) => (
          <Reveal key={p.id} delay={(i % 3) * 0.05}>
            <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group flex flex-col h-full rounded-2xl border border-slate-200 bg-white overflow-hidden transition-all hover:-translate-y-1 hover:shadow-xl">
              {p.cover && <div className="aspect-[16/9] overflow-hidden bg-slate-100"><img src={p.cover} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>}
              <div className="p-6 flex flex-col flex-1">
                <span className="text-xs font-semibold text-primary uppercase tracking-wider">{p.category}</span>
                <h3 className="mt-2 font-display text-lg font-bold tracking-tight leading-snug">{p.title}</h3>
                <p className="mt-2 text-sm text-slate-600 flex-1 leading-relaxed">{p.excerpt}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary">Read article <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
              </div>
            </Link>
          </Reveal>
        ))}
        {data.items.length === 0 && <p className="text-slate-400 col-span-full text-center py-12">No articles yet — check back soon.</p>}
      </section>
    </PublicLayout>
  );
}
